'use client';

import { PreviewCard } from '@base-ui/react/preview-card';
import { Bell, BookOpen, Calendar, ExternalLink } from 'lucide-react';
import Image from 'next/image';
import Link from 'next/link';

import { cn } from '@/shared/utils/cn';

import { CourseCardCartIconButton } from './CourseCardCartIconButton';
import { CourseCardFavoriteButton } from './CourseCardFavoriteButton';
import { CourseCardMetaRow } from './CourseCardMetaRow';
import { CourseCardPopover } from './CourseCardPopover';
import { CourseCardAllianceNote } from './CourseCardAllianceNote';
import { CourseCardAllianceTag } from './CourseCardAllianceTag';
import { CourseCardPresaleTag } from './CourseCardPresaleTag';
import { CourseCardPrice } from './CourseCardPrice';
import { CourseCardPromoBar } from './CourseCardPromoBar';
import { CourseCardRow } from './CourseCardRow';
import { CourseCardSelectionControl } from './CourseCardSelectionControl';
import { CourseCardUnavailableBadge } from './CourseCardUnavailableBadge';
import { useCourseCardWidget } from '../hooks/useCourseCardWidget';
import type { CourseCardWidgetProps } from '../types/course-card';

export function CourseCardWidget({
  course,
  variant = 'card',
  isFavorite = false,
  isInCart = false,
  isLoggedIn = false,
  isCyberActive = false,
  showFavoriteButton = true,
  isSelectable = false,
  isSelected = false,
  selectionName = 'course[]',
  selectionValue,
  hideActions = false,
  hidePrice = false,
  prioritizeImage = false,
  unavailableActionLabel = 'Avísame de la nueva versión',
  actionLabel = 'Inscribirme',
  checkoutLabel = 'Ir a pagar',
  cartIntent = 'default',
  selectedCourseId,
  onAddToCart,
  onAddToCartAnimationComplete,
  onCheckout,
  onFavoriteToggle,
  onSelectionChange,
  onNavigate,
  onUnavailableAction,
}: CourseCardWidgetProps) {
  const {
    cartState,
    handleAddToCartAnimationComplete,
    handleCardCartAction,
    handleCartAction,
    handleFavoriteToggle,
    handleNavigate,
    handleSelectionChange,
    href,
    imageError,
    isDesktopHover,
    isSelectionChecked,
    primaryCategoryLabel,
    promoLabel,
    resolvedSelectionValue,
    setImageError,
    startLabel,
    teachersSummary,
  } = useCourseCardWidget({
    course,
    variant,
    isFavorite,
    isInCart,
    isLoggedIn,
    showFavoriteButton,
    isSelectable,
    isSelected,
    selectionName,
    selectionValue,
    hideActions,
    actionLabel,
    checkoutLabel,
    cartIntent,
    selectedCourseId,
    onAddToCart,
    onAddToCartAnimationComplete,
    onCheckout,
    onFavoriteToggle,
    onSelectionChange,
    onNavigate,
  });
  const isDisabled = course.canPurchase === false;
  const hasCornerControl = isSelectable || (showFavoriteButton && isLoggedIn);
  const hasPromo = Boolean(promoLabel) && !isDisabled;

  const thumbnail =
    course.imageUrl && !imageError ? (
      <>
        <Image
          fill
          unoptimized
          alt={course.title}
          fetchPriority={prioritizeImage ? 'high' : undefined}
          loading={prioritizeImage ? 'eager' : undefined}
          className="object-cover transition-transform duration-300 ease-out group-hover:scale-[1.035] motion-reduce:transition-none motion-reduce:group-hover:scale-100"
          sizes="(max-width: 768px) 100vw, (max-width: 1280px) 50vw, 33vw"
          src={course.imageUrl}
          onError={() => setImageError(true)}
        />
        <div className="absolute inset-0 bg-linear-to-b from-black/10 via-transparent to-black/5" />
      </>
    ) : (
      <div className="absolute inset-0 flex items-center justify-center bg-adipa-surface-lavender">
        <BookOpen aria-hidden="true" className="text-(--color-primary-500) h-10 w-10" />
      </div>
    );

  if (variant === 'row') {
    return (
      <CourseCardRow
        cartState={cartState}
        course={course}
        cyberActive={isCyberActive}
        isFavorite={isFavorite}
        isLoggedIn={isLoggedIn}
        showFavoriteButton={showFavoriteButton}
        onCartAction={handleCartAction}
        onFavoriteToggle={handleFavoriteToggle}
        onNavigate={handleNavigate}
        onCartFlashAnimationComplete={handleAddToCartAnimationComplete}
        isSelectable={isSelectable}
        isSelected={isSelectionChecked}
        selectionName={selectionName}
        selectionValue={resolvedSelectionValue}
        hideActions={hideActions}
        hidePrice={hidePrice}
        prioritizeImage={prioritizeImage}
        actionLabel={actionLabel}
        checkoutLabel={checkoutLabel}
        onSelectionChange={handleSelectionChange}
      />
    );
  }

  const hasUnavailableAction =
    !hideActions && course.canPurchase === false && Boolean(onUnavailableAction);

  const detailLink = hideActions ? null : (
    <Link
      className={cn(
        'border-(--color-primary-500) text-(--color-primary-600) hover:bg-(--color-primary-500) hover:border-(--color-primary-500) hover:text-adipa-primary-foreground focus-visible:ring-(--color-primary-500) inline-flex h-11 cursor-pointer items-center justify-center gap-1.5 rounded-lg border bg-white px-3 text-[0.82rem] font-semibold transition-[background-color,border-color,color,box-shadow] duration-500 ease-[cubic-bezier(0.22,1,0.36,1)] hover:shadow-[0_10px_22px_rgba(112,78,253,0.18)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 motion-reduce:transition-none',
        hasUnavailableAction ? 'w-full' : 'flex-1',
      )}
      href={href}
      onClick={(event) => {
        event.stopPropagation();
        handleNavigate(event);
      }}
      onKeyDown={(event) => event.stopPropagation()}
    >
      <ExternalLink aria-hidden="true" className="h-3.5 w-3.5 shrink-0" strokeWidth={2.1} />
      Ver detalle
    </Link>
  );

  const unavailableActionLink =
    hasUnavailableAction && onUnavailableAction ? (
      <button
        className="bg-adipa-primary text-adipa-primary-foreground hover:bg-adipa-primary-hover focus-visible:ring-(--color-primary-500) inline-flex h-11 w-full cursor-pointer items-center justify-center gap-1.5 rounded-lg px-3 text-[0.82rem] font-semibold transition-colors duration-300 ease-out focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 motion-reduce:transition-none"
        type="button"
        onClick={(event) => {
          event.stopPropagation();
          onUnavailableAction(course);
        }}
        onKeyDown={(event) => event.stopPropagation()}
      >
        <Bell aria-hidden="true" className="h-3.5 w-3.5 shrink-0" strokeWidth={2.1} />
        {unavailableActionLabel}
      </button>
    ) : null;

  return (
    <>
      <PreviewCard.Root>
        <article
          className={cn(
            'border-(--color-border-subtle) group relative flex h-full cursor-default flex-col overflow-hidden rounded-xl border bg-white shadow-none',
            'hover:border-(--border-accent-soft) transition-[border-color,box-shadow] duration-500 ease-[cubic-bezier(0.22,1,0.36,1)] hover:shadow-[0_6px_18px_rgba(19,1,63,0.08)]',
            'focus-within:border-(--border-accent-soft) focus-within:shadow-[0_6px_18px_rgba(19,1,63,0.08)] motion-reduce:transition-none',
          )}
        >
          {/* PreviewCard.Trigger wraps only image + info, NOT the action button */}
          <PreviewCard.Trigger
            closeDelay={120}
            delay={430}
            render={<div className="flex flex-1 flex-col" />}
          >
            <div className="bg-(--color-muted) relative aspect-16/10 overflow-hidden">
              <div className={cn('absolute inset-0 cursor-default', isDisabled && 'grayscale')}>
                {thumbnail}
              </div>
              {hasPromo ? (
                <CourseCardPromoBar
                  className="absolute inset-x-0 top-0"
                  deadline={course.promoDeadline}
                  label={promoLabel as string}
                />
              ) : null}
              {isDisabled ? (
                <CourseCardUnavailableBadge className="absolute bottom-3 left-3 z-10 max-w-[calc(100%-4.75rem)]" />
              ) : course.presale || primaryCategoryLabel || course.allianceLabel ? (
                <div
                  className={cn(
                    'absolute bottom-3 left-3 z-10 flex items-end justify-between gap-1.5',
                    hasCornerControl ? 'w-[calc(100%-4.75rem)]' : 'w-[calc(100%-1.5rem)]',
                  )}
                >
                  <div className="flex min-w-0 flex-col items-start gap-1.5">
                    {course.presale ? <CourseCardPresaleTag /> : null}
                    {primaryCategoryLabel ? (
                      <span className="text-(--color-primary-700) border-(--color-border-subtle) inline-flex h-6 max-w-full items-center rounded-lg border bg-white/95 px-3 text-[0.64rem] font-bold leading-tight backdrop-blur-sm">
                        {primaryCategoryLabel}
                      </span>
                    ) : null}
                  </div>
                  {course.allianceLabel ? (
                    <CourseCardAllianceTag label={course.allianceLabel} />
                  ) : null}
                </div>
              ) : null}
              {/* REGLA: checkbox XOR favorito (nunca ambos, para no pelear espacio).
                  Ambos van abajo-derecha, alineados al badge (abajo-izquierda). */}
              {isSelectable ? (
                <CourseCardSelectionControl
                  checked={isSelectionChecked}
                  className="absolute bottom-3 right-3 z-20"
                  label={`Seleccionar ${course.title}`}
                  name={selectionName}
                  value={resolvedSelectionValue}
                  onChange={handleSelectionChange}
                />
              ) : showFavoriteButton && isLoggedIn ? (
                <CourseCardFavoriteButton
                  className="absolute bottom-3 right-3 z-20"
                  isFavorite={isFavorite}
                  onToggle={handleFavoriteToggle}
                />
              ) : null}
            </div>

            <div
              className="flex flex-1 flex-col gap-2 rounded-xl p-4 pb-0 cursor-default"
            >
              <div className="space-y-1">
                <h3 className="overflow-hidden text-[0.94rem] font-bold leading-snug">
                  <Link
                    className={cn(
                      'text-navy duration-180 hover:text-(--color-primary-700) focus-visible:ring-(--color-primary-500) group-hover:text-(--color-primary-700) cursor-pointer overflow-hidden transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2',
                      '[-webkit-box-orient:vertical] [-webkit-line-clamp:2] [display:-webkit-box]',
                    )}
                    href={href}
                    onClick={(event) => {
                      event.stopPropagation();
                      handleNavigate(event);
                    }}
                    onKeyDown={(event) => event.stopPropagation()}
                  >
                    {course.title}
                  </Link>
                </h3>
                {teachersSummary ? (
                  <p className="text-(--text-secondary) truncate text-[0.82rem] leading-tight">
                    {teachersSummary}
                  </p>
                ) : null}
              </div>


              <div className="mt-1 space-y-2">
                <CourseCardMetaRow course={course} />

                {startLabel && !isDisabled ? (
                  <div className="text-muted-text space-y-1 text-[0.75rem] font-medium">
                    <p className="flex min-h-5 items-center gap-1">
                      <Calendar aria-hidden="true" className="text-muted-text h-3 w-3" />
                      {startLabel}
                    </p>
                  </div>
                ) : null}
                {course.allianceLabel ? (
                  <CourseCardAllianceNote label={course.allianceLabel} />
                ) : null}
              </div>

              {!isDisabled && !hidePrice ? (
                <div className="mt-auto space-y-1.5 pt-4">
                  <CourseCardPrice course={course} cyberActive={isCyberActive} />
                </div>
              ) : null}
            </div>
          </PreviewCard.Trigger>

          {/* El contenido (trigger) es flex-1 y el precio usa mt-auto, así que el
              precio queda siempre al fondo, justo sobre el botón, en todas las cards. */}

          {/* Action buttons outside the trigger — z-10 ensures they're always above title link's hit area */}
          {detailLink ? (
            <div
              className={cn(
                'relative z-10 flex gap-2 px-4 pb-4 pt-3',
                unavailableActionLink && 'flex-col',
              )}
            >
              {detailLink}
              {unavailableActionLink}
              {course.canPurchase !== false ? (
                <CourseCardCartIconButton
                  aria-label={
                    cartState === 'pay' ? checkoutLabel : `${actionLabel} en ${course.title}`
                  }
                  cartState={cartState}
                  onAddingAnimationComplete={handleAddToCartAnimationComplete}
                  onClick={handleCardCartAction}
                />
              ) : null}
            </div>
          ) : (
            /* Sin acciones (ej. cross-sell): mantiene el respiro inferior de la card */
            <div aria-hidden="true" className="pb-4" />
          )}
        </article>

        {isDesktopHover ? (
          <PreviewCard.Portal>
            <PreviewCard.Positioner
              sticky
              align="start"
              className="z-[9999] max-w-[calc(100vw-32px)]"
              collisionAvoidance={{ side: 'flip', align: 'shift', fallbackAxisSide: 'none' }}
              collisionPadding={{ top: 152, right: 16, bottom: 16, left: 16 }}
              positionMethod="fixed"
              side="right"
              sideOffset={14}
            >
              <PreviewCard.Popup className="origin-(--transform-origin) data-ending-style:scale-95 data-ending-style:opacity-0 data-starting-style:scale-95 data-starting-style:opacity-0 transition-[transform,scale,opacity] duration-150">
                <CourseCardPopover
                  cartState={cartState}
                  course={course}
                  hidePrice={hidePrice}
                  onCartAction={handleCartAction}
                  onCartFlashAnimationComplete={handleAddToCartAnimationComplete}
                  onNavigate={handleNavigate}
                />
              </PreviewCard.Popup>
            </PreviewCard.Positioner>
          </PreviewCard.Portal>
        ) : null}
      </PreviewCard.Root>
    </>
  );
}

export { CourseCardWidget as CourseCard };
export type CourseCardProps = CourseCardWidgetProps;
